import React, { useCallback, useEffect, useState } from "react";
import { logError } from "@/lib/logger";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, ShieldCheck, Trash, PencilSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import GoogleConnectCard from "@/components/GoogleConnectCard";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";

const ROLES = ["admin", "recruiter", "hiring_manager"];

const ROLE_STYLE = {
    admin: "bg-[#FAF5FF] text-[#7E22CE]",
    recruiter: "bg-[#EEF2FF] text-[#3730A3]",
    hiring_manager: "bg-[#ECFDF5] text-[#059669]",
};

const empty = { name: "", email: "", password: "", role: "recruiter" };

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);
    // Feb 9, 2026 — hide Google Calendar & Meet integration by default.
    // Only visible once admin flips `google_calendar_sync_enabled` in
    // Workspace Settings (/settings → "Enable Google Calendar sync").
    const [googleSyncEnabled, setGoogleSyncEnabled] = useState(false);
    useEffect(() => {
        api.get("/settings/calendar")
            .then((r) => setGoogleSyncEnabled(!!r.data?.google_calendar_sync_enabled))
            .catch(() => setGoogleSyncEnabled(false));
    }, []);

    const load = useCallback(
        () =>
            api
                .get("/users")
                .then((r) => setUsers(r.data))
                .catch((e) => logError("Failed to load users:", e)),
        [],
    );
    useEffect(() => {
        load();
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSaving(true);
        try {
            if (editing) {
                const body = {
                    name: form.name,
                    role: form.role,
                    ...(form.password ? { password: form.password } : {}),
                };
                await api.patch(`/users/${editing}`, body);
            } else {
                // V1.1 — no password on create. Backend sends the invite email;
                // invitee sets their own password via /accept-invite?token=...
                const { data } = await api.post("/users", {
                    name: form.name, email: form.email, role: form.role,
                });
                if (data?.invite_status === "pending") {
                    toast.success(`Invite email sent to ${form.email}. They'll receive a link to set their password.`);
                } else {
                    toast.success(`${form.name} added successfully.`);
                }
            }
            setForm(empty);
            setEditing(null);
            setShowForm(false);
            load();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (u) => {
        setEditing(u.id);
        setForm({ name: u.name, email: u.email, password: "", role: u.role });
        setShowForm(true);
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            load();
        } catch (e) {
            alert(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const resendInvite = async (u) => {
        try {
            await api.post(`/users/${u.id}/resend-invite`);
            toast.success(`Invite resent to ${u.email}.`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1600px] mx-auto" data-testid="users-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Team & access · Admin & hiring manager
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                        Team.
                    </h1>
                </div>
                <div className="flex items-center gap-2 self-start sm:self-auto">
                    <a
                        href="/settings/roles"
                        data-testid="view-role-matrix-link"
                        className="border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink px-4 py-2.5 sm:px-5 sm:py-3 font-bold tracking-wide text-xs flex items-center gap-2 transition-colors"
                    >
                        <ShieldCheck size={14} weight="bold" /> Role permissions
                    </a>
                    <button
                        onClick={() => {
                            setEditing(null);
                            setForm(empty);
                            setShowForm(true);
                        }}
                        data-testid="open-new-user-button"
                        className="bg-cobalt hover:bg-cobalt-dark text-white px-5 py-2.5 sm:px-6 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 transition-colors"
                    >
                        <Plus size={14} weight="bold" /> Invite teammate
                    </button>
                </div>
            </div>

            {googleSyncEnabled && (
                <div className="mb-6 sm:mb-8" data-testid="team-google-integration-section">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2">
                        Your integrations
                    </p>
                    <GoogleConnectCard />
                </div>
            )}

            <div className="bg-white border border-[#E5E5E5]">
                <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E5E5E5] text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                    <div className="col-span-5">Member</div>
                    <div className="col-span-3">Role</div>
                    <div className="col-span-2">Joined</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {users.map((u) => (
                    <div
                        key={u.id}
                        data-testid={`user-row-${u.id}`}
                        className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-4 sm:px-6 py-4 border-b border-[#E5E5E5] last:border-b-0 items-center"
                    >
                        <div className="col-span-5">
                            <p className="font-bold text-ink">
                                {u.name}{" "}
                                {u.id === me?.id && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-cobalt">
                                        (you)
                                    </span>
                                )}
                            </p>
                            <p className="text-xs text-[#525252]">{u.email}</p>
                        </div>
                        <div className="col-span-3">
                            <span
                                className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 ${ROLE_STYLE[u.role] || ""}`}
                            >
                                <ShieldCheck size={10} weight="fill" />
                                {u.role.replace("_", " ")}
                            </span>
                            {u.invite_status === "pending" && (
                                <span
                                    data-testid={`user-invite-status-${u.id}`}
                                    className="ml-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#FEF3C7] text-[#B45309]"
                                    title="Awaiting password setup"
                                >
                                    Pending
                                </span>
                            )}
                        </div>
                        <div className="col-span-2 text-xs text-[#525252]">
                            {new Date(u.created_at).toLocaleDateString()}
                        </div>
                        <div className="col-span-2 flex justify-start sm:justify-end gap-2">
                            {u.invite_status === "pending" && (
                                <button
                                    onClick={() => resendInvite(u)}
                                    data-testid={`resend-invite-${u.id}`}
                                    className="p-2 border border-[#E5E5E5] hover:border-cobalt hover:text-cobalt"
                                    aria-label="Resend invite"
                                    title="Resend invite email"
                                >
                                    <PaperPlaneTilt size={14} weight="bold" />
                                </button>
                            )}
                            <button
                                onClick={() => startEdit(u)}
                                data-testid={`edit-user-${u.id}`}
                                className="p-2 border border-[#E5E5E5] hover:border-ink"
                                aria-label="Edit"
                            >
                                <PencilSimple size={14} weight="bold" />
                            </button>
                            {u.id !== me?.id && (
                                <button
                                    onClick={() => remove(u)}
                                    data-testid={`delete-user-${u.id}`}
                                    className="p-2 border border-[#E5E5E5] hover:border-[#DC2626] hover:text-[#DC2626]"
                                    aria-label="Delete"
                                >
                                    <Trash size={14} weight="bold" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {showForm && (
                <div
                    className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 flex items-center justify-center p-4"
                    onClick={() => setShowForm(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        data-testid="user-form"
                        className="bg-white border border-[#E5E5E5] w-full max-w-md"
                    >
                        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">
                                {editing ? "Edit teammate" : "Invite teammate"}
                            </h2>
                            <button
                                onClick={() => setShowForm(false)}
                                className="p-2 hover:bg-[#F5F5F5]"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Full name
                                </label>
                                <input
                                    data-testid="user-name-input"
                                    value={form.name}
                                    required
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Email
                                </label>
                                <input
                                    data-testid="user-email-input"
                                    type="email"
                                    value={form.email}
                                    required
                                    disabled={!!editing}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt disabled:bg-[#F5F5F5] disabled:text-[#A3A3A3]"
                                />
                            </div>
                            {editing && (
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        New password (optional)
                                    </label>
                                    <PasswordInput
                                        data-testid="user-password-input"
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        autoComplete="new-password"
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt"
                                    />
                                </div>
                            )}
                            {!editing && (
                                <div className="bg-[#EEF2FF] border border-[#C7D2FE] px-4 py-3 text-xs text-[#3730A3] leading-relaxed">
                                    We'll email <strong>{form.email || "them"}</strong> a secure invite link. They set their own password and log in — no password to share manually.
                                </div>
                            )}
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Role
                                </label>
                                <select
                                    data-testid="user-role-select"
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt"
                                >
                                    {ROLES.map((r) => (
                                        <option key={r} value={r}>
                                            {r.replace("_", " ")}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-[#525252] mt-2 leading-relaxed">
                                    <span className="font-bold">Admin</span>: full access + team mgmt
                                    · <span className="font-bold">Recruiter</span>: CRUD jobs &amp;
                                    candidates · <span className="font-bold">Hiring manager</span>:
                                    read-only + can add interview notes
                                </p>
                            </div>
                            {err && (
                                <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3">
                                    {err}
                                </div>
                            )}
                            <button
                                data-testid="user-submit-button"
                                disabled={saving}
                                type="submit"
                                className="w-full bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-6 py-3 font-bold tracking-wide text-sm"
                            >
                                {saving ? "Saving…" : editing ? "Save changes" : "Send invite"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
